/* @flow */

import { CMSConsumer } from 'CoreConsumers';
import SearchResultsViewMode from '../components/SearchResults/SearchResultsViewMode';
import { TemplateMarketPropertyKeys } from 'CoreTemplates/TemplateMarketProperties';
import type { $Configuration } from 'CoreTypes/Configuration';
import type { $Translation } from 'CoreTypes/Translation';
import type { $WidgetField } from 'CoreTypes/WidgetField';
import type { $WidgetEnvConfiguration } from '../types/WidgetEnvConfiguration';
import type { $WidgetSettings } from '../types/WidgetSettings';
import { getConfigurationMarketProperty } from 'CoreUtils/WidgetUtils';
import { getConfigurationTranslations } from 'CoreUtils/WidgetUtils';
import { getCMSEndpointURL } from 'CoreUtils/EndpointUtils'
import { getEnvConfiguration } from '../environments';

const GET_CONFIGURATION_REQUEST = 'dsw/data/GET_CONFIGURATION_REQUEST';
const GET_CONFIGURATION_SUCCESS = 'dsw/data/GET_CONFIGURATION_SUCCESS';
const GET_CONFIGURATION_FAILURE = 'dsw/data/GET_CONFIGURATION_FAILURE';
const PREVIEW_FORM_FIELD_CHANGE = 'dsw/data/PREVIEW_FORM_FIELD_CHANGE';
const RESET_WIDGET_DATA = 'dsw/data/RESET_WIDGET_DATA';

export type $Action =
  | { type: 'dsw/data/GET_CONFIGURATION_REQUEST' }
  | { type: 'dsw/data/GET_CONFIGURATION_SUCCESS', configuration: $Configuration, language: string }
  | { type: 'dsw/data/GET_CONFIGURATION_FAILURE' }
  | { type: 'dsw/data/PREVIEW_FORM_FIELD_CHANGE', field: $WidgetField, value: string }
  | { type: 'dsw/data/RESET_WIDGET_DATA' };

export type $State = {
  configuration: $Configuration,
  translations: Array<$Translation>
};

export const initialState = {
  configuration: {},
  translations: []
};

export default function reducer(state: $State, action: $Action) {
  if (!state) state = initialState;
  switch (action.type) {
    case GET_CONFIGURATION_SUCCESS: {
      const { configuration, language } = action;
      const translations = getConfigurationTranslations(configuration, language || '');
      return {
        ...state,
        configuration,
        translations
      };
    }

    case GET_CONFIGURATION_FAILURE:
      return {
        ...state,
        configuration: {},
        translations: []
      };

    case PREVIEW_FORM_FIELD_CHANGE: {
      return updateStateByFieldKey(state, action.field, action.value);
    }

    case RESET_WIDGET_DATA:
      return {
        ...state,
        search: {
          results: [],
          status
        },
        configuration: {},
        translations: []
      };

    default:
      return state;
  }
}

export const updateStateByFieldKey = (initialState: $State, field: $WidgetField, value: string) => {
  const state = { ...initialState };
  if (field.translatable && state.translations && state.translations.length > 0) {
    const translations = state.translations;
    const translation = translations.find(obj => obj.key === field.fkey);
    if (translation) {
      translation.translation = value || '';
    } else {
      translations.push({ key: field.fkey, name: '', translation: value });
    }
  }
  else if (!field.translatable) {
    const property = state.configuration && state.configuration.properties &&
      state.configuration.properties.find(property => property.key === field.fkey);
    if (property) property.value = value;
  }
  return state;
};

export const getConfigurationData = (
  settings: $WidgetSettings, configId: string, language: string, market: string
) => async (dispatch: Function) => {
  dispatch(getConfigurationDataRequest());
  const envConfig: $WidgetEnvConfiguration = getEnvConfiguration(settings.env);
  const cms = new CMSConsumer(getCMSEndpointURL(settings, envConfig));
  try {
    const { data } = await cms.getConfiguration(configId, language, market);

    if (data) {
      let configuration = { ...data };

      const resultsViewMode = getConfigurationMarketProperty(
        configuration, market, TemplateMarketPropertyKeys.DSW_SEARCH_RESULTS_VIEW_MODE);

      if (!configuration.marketProperties)
        configuration.marketProperties = {};
      if (configuration.marketProperties && !configuration.marketProperties[market])
        configuration.marketProperties[market] = {};
      if (configuration.marketProperties && configuration.marketProperties[market] && !resultsViewMode)
        configuration.marketProperties[market][TemplateMarketPropertyKeys.DSW_SEARCH_RESULTS_VIEW_MODE] =
          SearchResultsViewMode.COMBO_MODE_MAP.key;

      dispatch(getConfigurationDataSuccess(configuration, language));
    }
    else dispatch(getConfigurationDataFailure());
    return data;
  } catch (error) {
    dispatch(getConfigurationDataFailure());
  }
};

export const getConfigurationDataRequest = () => ({
  type: GET_CONFIGURATION_REQUEST
});

export const getConfigurationDataSuccess = (configuration: $Configuration, language: string) => ({
  type: GET_CONFIGURATION_SUCCESS,
  configuration,
  language
});

export const getConfigurationDataFailure = () => ({
  type: GET_CONFIGURATION_FAILURE
});

export const changePreviewFormField = (field: $WidgetField, value: string) => ({
  type: PREVIEW_FORM_FIELD_CHANGE,
  field,
  value
});

export const resetWidgetData = () => ({
  type: RESET_WIDGET_DATA
});
